"use client";

import CallToAction from "@/components/CallToAction";
import FAQs from "@/components/FAQs";
import HighlightTitle from "@/components/HighlightTitle";
import { Image } from "@/components/Image";
import Page from "@/components/Page";
import Section from "@/components/ui/Section";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { buttonKey, textKey } from "@/lib/list-keys";
import { cn, tinaField, useTina } from "@/lib/utils";
import Link from "next/link";

export default function ProductPage(props) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const { data: homeData } = useTina({
    query: props.homeQuery,
    variables: props.homeVariables,
    data: props.homeData,
  });

  const { header, featureDetails } = data.product;

  const faqs = homeData?.home?.faqs;
  const cta = homeData?.home?.cta;

  return (
    <Page defaultTinaField={data.product}>
      <Header tinaFieldData={data.product.header} {...header} />
      <FeatureDetails tinaFieldData={data.product.featureDetails} {...featureDetails} />
      {faqs && <FAQs tinaFieldData={homeData.home.faqs} {...faqs} />}
      {cta && <CallToAction tinaFieldData={homeData.home.cta} {...cta} />}
    </Page>
  );
}

function Header({ tinaFieldData, title, subtitle, ctas, image }) {
  return (
    <Section as="header" backgroundPattern={true} wrapperClassName="!pt-12 !pb-0">
      <div className="flex flex-col gap-6 md:gap-10">
        <div className="flex flex-col gap-4 md:gap-10 p-6 lg:p-12 relative">
          <div className="absolute w-16 h-10 border-[3px_0_0px_3px] border-primary top-0 left-0" />
          <div className="flex flex-col max-w-[880px]">
            <HighlightTitle as="h1" className="h0" content={title} data-tina-field={tinaField(tinaFieldData, "title")} />
            {ctas?.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-4 mt-4 lg:mt-8">
                {ctas.map((btn, index) => (
                  <Button
                    key={buttonKey(btn, index)}
                    data-tina-field={tinaField(ctas[index], "label")}
                    asChild
                    size="lg"
                    arrow={index === 0}
                    variant={index === 0 ? "default" : "outline"}
                    className="w-full sm:w-auto"
                  >
                    <Link href={btn.link || "/"}>{btn.label}</Link>
                  </Button>
                ))}
              </div>
            )}
          </div>
          {subtitle && (
            <p data-tina-field={tinaField(tinaFieldData, "subtitle")} className="p-lg text-balance max-w-[760px]">
              {subtitle}
            </p>
          )}
        </div>
        <Image
          src={image}
          alt="SlánDocs platform overview"
          wrapperClassName="aspect-[16/6] w-[calc(100%+32px)] md:w-full max-md:-mx-4 md:w-full md:mx-auto"
          className="object-contain object-bottom"
          data-tina-field={tinaField(tinaFieldData, "image")}
          sizes="100vw"
          priority
          fetchPriority="high"
          loading="eager"
        />
      </div>
    </Section>
  );
}

function FeatureDetails({ tinaFieldData, pretitle, title, subtitle, features }) {
  return (
    <Section wrapperClassName="overflow-visible">
      <div className="flex flex-col items-start gap-4 mb-12 lg:mb-16 max-w-[840px]">
        {pretitle && (
          <Badge variant="outline" data-tina-field={tinaField(tinaFieldData, "pretitle")}>
            {pretitle}
          </Badge>
        )}
        {title && (
          <h2 data-tina-field={tinaField(tinaFieldData, "title")} className="h1 text-balance">
            {title}
          </h2>
        )}
        {subtitle && (
          <p data-tina-field={tinaField(tinaFieldData, "subtitle")} className="p-lg">
            {subtitle}
          </p>
        )}
      </div>
      {features?.length > 0 && (
        <div className="flex flex-col">
          {features.map((feature, index) => {
            return (
              <div
                key={textKey(feature.title, index)}
                className={cn("grid grid-cols-1 lg:grid-cols-[1fr_500px] gap-8 lg:gap-16 border-t border-border py-8 lg:py-16", "lg:sticky bg-background")}
                style={{ top: `calc(5rem + ${index} * 3.5rem)`, zIndex: index }}
              >
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <span className="text-lg lg:-translate-y-13">{String(index + 1).padStart(2, "0")}</span>
                    <h3 className="h2 text-title text-balance" data-tina-field={tinaField(features[index], "title")}>
                      {feature.title}
                    </h3>
                  </div>
                  {feature.text && (
                    <p className="p-lg" data-tina-field={tinaField(features[index], "text")}>
                      {feature.text}
                    </p>
                  )}
                </div>
                <Card className="p-8">
                  <Image
                    src={feature?.image}
                    alt={feature.title || ""}
                    wrapperClassName="aspect-video w-full overflow-hidden max-lg:max-h-[200px]"
                    className="object-contain"
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    data-tina-field={tinaField(features[index], "image")}
                  />
                </Card>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}
